import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Check, X, Expand } from "lucide-react";
import { ROOM_TABS, WHATSAPP_LINK } from "@/data/content";

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function Rooms() {
  const [tab, setTab] = useState(ROOM_TABS[0]);
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="rooms" className="bg-cream px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-maroon-accent">
              Stay With Us · <span className="font-kannada normal-case tracking-normal">ವಿಶ್ರಾಂತಿ</span>
            </p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-forest-deep sm:text-4xl lg:text-4xl">
              Rooms dressed in warm wood <span className="italic text-gold">& quiet light</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-stone-600">
            Every room is air-conditioned, freshly linen-d and steps from the highway — yet
            hushed. Reserve in one message.
          </p>
        </motion.div>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="mt-10 flex flex-wrap gap-2"
        >
          {ROOM_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t)}
              data-testid={t.testid}
              className={`rounded-full border px-5 py-2.5 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                tab.id === t.id
                  ? "border-forest bg-forest text-gold-light"
                  : "border-forest/25 bg-transparent text-forest hover:border-forest/60"
              }`}
            >
              {t.label}
            </button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 grid gap-8 lg:grid-cols-5"
          >
            <div className="lg:col-span-2">
              <p className="font-kannada text-sm text-maroon-accent">{tab.kannada}</p>
              <p className="mt-3 text-base leading-relaxed text-stone-700">{tab.blurb}</p>
              <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3">
                {tab.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-forest-deep">
                    <Check className="h-4 w-4 shrink-0 text-gold" strokeWidth={3} />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={WHATSAPP_LINK(`Hello Hotel Anil Palace! I'm interested in the ${tab.label}. Please share availability and tariff.`)}
                target="_blank"
                rel="noreferrer"
                data-testid="room-enquiry-modal-trigger"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-maroon px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-amber-100 transition-all duration-300 hover:scale-[1.03] hover:bg-maroon-accent"
              >
                <MessageCircle className="h-4 w-4" />
                Book this room on WhatsApp
              </a>
            </div>

            <div
              className={`grid gap-5 lg:col-span-3 ${
                tab.images.length === 3 ? "sm:grid-cols-2" : "sm:grid-cols-2"
              }`}
            >
              {tab.images.map((img, i) => (
                <button
                  key={img.src + i}
                  onClick={() => setLightbox(img)}
                  data-testid={`room-photo-${tab.id}-${i}`}
                  className={`group relative overflow-hidden rounded-lg gold-frame ${
                    tab.images.length === 3 && i === 0 ? "sm:col-span-2 aspect-[16/9]" : "aspect-[4/3]"
                  }`}
                >
                  <img
                    src={img.src}
                    alt={img.caption}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-forest-dark/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  <span className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-left text-xs font-semibold text-amber-50 opacity-0 transition-all duration-500 group-hover:opacity-100">
                    {img.caption}
                    <Expand className="h-4 w-4 shrink-0 text-gold-light" />
                  </span>
                </button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            data-testid="room-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center bg-forest-dark/90 p-4 backdrop-blur-md"
          >
            <motion.figure
              initial={{ scale: 0.92, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 12 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[85vh] max-w-4xl"
            >
              <img
                src={lightbox.src}
                alt={lightbox.caption}
                className="max-h-[78vh] w-full rounded-md object-contain gold-frame"
              />
              <figcaption className="mt-3 text-center font-display text-lg italic text-amber-100">
                {lightbox.caption}
              </figcaption>
              <button
                onClick={() => setLightbox(null)}
                data-testid="room-lightbox-close"
                aria-label="Close photo"
                className="absolute -right-2 -top-2 flex h-9 w-9 items-center justify-center rounded-full bg-gold text-forest-dark transition-transform hover:scale-110"
              >
                <X className="h-4 w-4" strokeWidth={3} />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
